import { defineStore } from 'pinia';
import { notify } from 'notiwind';
import { usePage } from '@inertiajs/vue3';
import { generateUUID } from '@/utils/uuid.js';
import { Apii } from '@/services/api.js';

const ApiService = new Apii();

export const useDashboardStore = defineStore('dashboard', {
    state: () => ({
        isEditMode: false,
        widgetPreferences: [],
        originalWidgetPreferences: [],
        widgetPreferencesSaving: false,
        loading: false,
        users: [],
        user: null,
        walletId: null,
        annualFees: 0,
        upcomingFees: [],
        card: {},
        points: 0,
        change: '--',
        direction: '',
        open: 0,
        closed: 0,
        count: 0,
        nextCardDate: '--',
        nextCardTitle: '',
        minSpendDeadlines: [],
        monthlyBenefits: {},
        expiringBenefits: [],
        walletCardCount: 0,
    }),
    actions: {
        toggleWidgetVisibility(widgetKey) {
            const widget = this.widgetPreferences.find(
                (w) => w.widget_key === widgetKey
            );
            if (widget) {
                widget.visible = !widget.visible;
            }
        },
        updateWidgetPositions(reorderedWidgets) {
            this.widgetPreferences = reorderedWidgets.map((widget, index) => ({
                ...widget,
                position: index,
            }));
        },
        enterEditMode() {
            this.originalWidgetPreferences = JSON.parse(
                JSON.stringify(this.widgetPreferences)
            );
            this.isEditMode = true;
        },
        cancelEditMode() {
            this.widgetPreferences = JSON.parse(
                JSON.stringify(this.originalWidgetPreferences)
            );
            this.isEditMode = false;
        },
        saveWidgetPreferences() {
            if (!this.user?.uuid) {
                this.isEditMode = false;
                return Promise.resolve();
            }

            this.widgetPreferencesSaving = true;

            return ApiService.apiCall('/dashboard/widget-preferences', 'put', {
                widgets: this.widgetPreferences,
                uuid: this.user.uuid,
            })
                .then((response) => {
                    if (response.data?.widgets) {
                        this.widgetPreferences = response.data.widgets;
                    }
                    this.isEditMode = false;
                    notify(
                        {
                            group: 'success',
                            title: 'Success',
                            text: 'Dashboard preferences saved.',
                        },
                        4000
                    );
                })
                .catch(() => {
                    notify(
                        {
                            group: 'error',
                            title: 'Error',
                            text: 'There was a problem saving your dashboard preferences.',
                        },
                        4000
                    );
                })
                .finally(() => {
                    this.widgetPreferencesSaving = false;
                });
        },
        getDefaultWidgetPreferences() {
            const defaultWidgetOrder = [
                'my_next_card',
                'minimum_spend_deadlines',
                'best_cards_by_category',
                'card_benefits_monthly',
                'card_benefits_expiring',
                'courses',
                'travel_goals',
                'companion_pass',
                'credit_score',
            ];

            return defaultWidgetOrder.map((widgetKey, index) => ({
                widget_key: widgetKey,
                position: index,
                visible: true,
            }));
        },
        fetchWidgetPreferences() {
            if (!this.user?.uuid) {
                this.widgetPreferences = this.getDefaultWidgetPreferences();
                return Promise.resolve();
            }

            return ApiService.apiCall(
                `/dashboard/widget-preferences?uuid=${encodeURIComponent(this.user.uuid)}`,
                'get'
            )
                .then((response) => {
                    const widgets = response?.data?.widgets;

                    this.widgetPreferences =
                        Array.isArray(widgets) && widgets.length > 0
                            ? widgets
                            : this.getDefaultWidgetPreferences();
                })
                .catch(() => {
                    this.widgetPreferences = this.getDefaultWidgetPreferences();
                    notify(
                        {
                            group: 'error',
                            title: 'Error',
                            text: 'There was a problem loading your dashboard preferences.',
                        },
                        4000
                    );
                });
        },
        getDashboardData() {
            this.getUsers();
        },
        resetDashboardData() {
            // Immediately clear all dashboard data when switching users
            this.widgetPreferences = this.getDefaultWidgetPreferences();
            this.annualFees = 0;
            this.upcomingFees = [];
            this.card = {};
            this.points = 0;
            this.change = '--';
            this.direction = '';
            this.open = 0;
            this.closed = 0;
            this.count = 0;
            this.nextCardDate = '--';
            this.nextCardTitle = '';
            this.minSpendDeadlines = [];
            this.monthlyBenefits = {};
            this.expiringBenefits = [];
            this.loading = true;
        },
        getDashboardCards() {
            // Execute all API calls in parallel for faster loading
            Promise.all([
                this.getTopCard(),
                this.getAnnualFees(),
                this.getUpcomingAnnualFees(),
                this.getNumberOfPoints(),
                this.getNumberOfCards(),
                this.getFiveTwentyFourCount(),
                this.getMinSpendDeadlines(),
                this.getMonthlyBenefits(),
                this.getExpiringBenefits(),
            ])
                .catch((error) => {
                    // Handle any errors from the parallel API calls
                    console.error('Error loading dashboard data:', error);
                    // Individual API methods already show user-facing error notifications
                })
                .finally(() => {
                    // Always reset loading state, even if some calls failed
                    this.loading = false;
                });
        },
        getUsers() {
            ApiService.apiCall('/wallets/users', 'get')
                .then((response) => {
                    this.users = response.data.users;
                    this.user = this.users[0];
                    this.users[0].active = true;
                    this.fetchWidgetPreferences();
                    this.getDashboardCards();
                })
                .catch((response) => {});
        },
        getUsersOnly() {
            // Fetch only user data without loading dashboard cards
            // Used on pages that need user info but not wallet/dashboard context
            return ApiService.apiCall('/wallets/users', 'get')
                .then((response) => {
                    this.users = response.data.users;
                    this.user = this.users[0];
                    this.users[0].active = true;
                    // Intentionally NOT calling getDashboardCards() here
                })
                .catch((response) => {
                    console.error('Error fetching users:', response);
                    throw response;
                });
        },
        getAnnualFees() {
            return ApiService.apiCall(
                '/dashboard/cards/annual-fees',
                'post',
                this.user
            )
                .then((response) => {
                    this.annualFees = response.data.annual_fees;
                })
                .catch((response) => {
                    notify(
                        {
                            group: 'error',
                            title: 'Error',
                            text: 'There was a problem trying to get annnual fees card.',
                        },
                        4000
                    );
                });
        },
        getUpcomingAnnualFees() {
            return ApiService.apiCall(
                '/dashboard/cards/upcoming-annual-fees',
                'post',
                this.user
            )
                .then((response) => {
                    this.upcomingFees = response.data.upcoming_fees;
                })
                .catch((response) => {
                    notify(
                        {
                            group: 'error',
                            title: 'Error',
                            text: 'There was a problem trying to get upcoming annual fees.',
                        },
                        4000
                    );
                });
        },
        getTopCard() {
            return ApiService.apiCall(
                '/dashboard/cards/top-card',
                'post',
                this.user
            )
                .then((response) => {
                    this.card = response.data;
                })
                .catch((response) => {
                    notify(
                        {
                            group: 'error',
                            title: 'Error',
                            text: 'There was a problem trying to get top card.',
                        },
                        4000
                    );
                });
        },
        getNumberOfPoints() {
            return ApiService.apiCall(
                '/dashboard/cards/number-of-points',
                'post',
                this.user
            )
                .then((response) => {
                    this.points = response.data.points;
                    this.change = response.data.prevChange;
                    this.direction = response.data.direction;
                })
                .catch((response) => {
                    notify(
                        {
                            group: 'error',
                            title: 'Error',
                            text: 'There was a problem trying to get number of points.',
                        },
                        4000
                    );
                });
        },
        getNumberOfCards() {
            return ApiService.apiCall(
                '/dashboard/cards/number-of-cards',
                'post',
                this.user
            )
                .then((response) => {
                    this.open = response.data.open ?? 0;
                    this.closed = response.data.closed ?? 0;
                })
                .catch((response) => {
                    notify(
                        {
                            group: 'error',
                            title: 'Error',
                            text: 'There was a problem trying to get number of cards.',
                        },
                        4000
                    );
                });
        },
        refreshCardCounts() {
            if (!this.user) {
                return Promise.resolve();
            }
            return this.getNumberOfCards();
        },
        getFiveTwentyFourCount() {
            return ApiService.apiCall(
                '/dashboard/cards/five-twenty-four',
                'post',
                this.user
            )
                .then((response) => {
                    this.count = response.data.count;
                    this.nextCardDate = response.data.next_card_date;
                    this.nextCardTitle = response.data.next_card_title;
                })
                .catch((response) => {
                    notify(
                        {
                            group: 'error',
                            title: 'Error',
                            text: 'There was a problem trying to get 5/24 count.',
                        },
                        4000
                    );
                });
        },
        getCardsForUser() {
            return ApiService.apiCall(
                '/dashboard/cards/user-cards',
                'post',
                this.user
            )
                .then((response) => {
                    return response.data.cards || [];
                })
                .catch((response) => {
                    notify(
                        {
                            group: 'error',
                            title: 'Error',
                            text: 'There was a problem trying to get user cards.',
                        },
                        4000
                    );
                    return [];
                });
        },
        updateUserPreference(preference) {
            this.loading = true;
            const user = this.users.find((u) => u.active);

            if (!user) {
                console.warn(
                    'No active user found when trying to update preference'
                );
                this.loading = false;
                return;
            }

            ApiService.apiCall(`/user-settings/${preference}`, 'put', {
                uuid: user.uuid,
            })
                .then(() => {
                    if (preference === 'start_guide_completed_at') {
                        user.start_guide_completed = true;
                    } else if (preference.startsWith('acknowledge-card-')) {
                        const cardId = preference.replace(
                            'acknowledge-card-',
                            ''
                        );

                        if (!user.acknowledged_cards) {
                            user.acknowledged_cards = [];
                        }

                        if (!user.acknowledged_cards.includes(cardId)) {
                            user.acknowledged_cards.push(cardId);
                        }
                    }
                })
                .catch((error) => {
                    console.error('Error updating the configuration:', error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        getMinSpendDeadlines() {
            if (!this.walletId) {
                return Promise.resolve();
            }

            return ApiService.apiCall(
                `/wallets/${this.walletId}/users/${this.user.uuid}/min-spend-deadlines`,
                'get'
            )
                .then((response) => {
                    this.minSpendDeadlines =
                        response.data.min_spend_cards || [];
                    this.walletCardCount = response.data.wallet_card_count;
                })
                .catch((response) => {
                    notify(
                        {
                            group: 'error',
                            title: 'Error',
                            text: 'There was a problem trying to get minimum spend deadlines.',
                        },
                        4000
                    );
                });
        },
        getMonthlyBenefits() {
            if (!this.walletId) {
                return Promise.resolve();
            }

            return ApiService.apiCall(
                `/wallets/${this.walletId}/users/${this.user.uuid}/monthly-benefits`,
                'get'
            )
                .then((response) => {
                    this.monthlyBenefits = response.data;
                })
                .catch((response) => {
                    notify(
                        {
                            group: 'error',
                            title: 'Error',
                            text: 'There was a problem trying to get monthly benefits.',
                        },
                        4000
                    );
                });
        },
        getExpiringBenefits() {
            if (!this.walletId) {
                return Promise.resolve();
            }

            return ApiService.apiCall(
                `/wallets/${this.walletId}/card-benefits?uuid=${this.user.uuid}`,
                'get'
            )
                .then((response) => {
                    const allBenefits = response.data.flat?.benefits || [];
                    const now = new Date();
                    const thirtyDaysFromNow = new Date(
                        now.getTime() + 30 * 24 * 60 * 60 * 1000
                    );

                    // Get freemium data
                    const isSubscribed =
                        response.data.freemium?.is_subscribed ?? true;
                    const trackedIds =
                        response.data.freemium?.tracked_connected_account_ids ||
                        [];

                    // For free users, filter to only show benefits from tracked cards
                    const benefits = isSubscribed
                        ? allBenefits
                        : allBenefits.filter((item) =>
                              trackedIds.includes(item.connected_account_id)
                          );

                    // Filter benefits that are expiring within 30 days and have remaining value
                    const expiringBenefits = benefits
                        .filter((item) => {
                            if (!item.period_end_date) return false;

                            const periodEndDate = new Date(
                                item.period_end_date
                            );
                            const benefitAmount = parseFloat(
                                item.benefit?.amount || 0
                            );
                            const usedAmount = parseFloat(
                                item.current_period_usage || 0
                            );
                            const amountLeft = benefitAmount - usedAmount;

                            // Only include if expires within 30 days and has value left
                            return (
                                periodEndDate >= now &&
                                periodEndDate <= thirtyDaysFromNow &&
                                amountLeft > 0
                            );
                        })
                        .map((item) => {
                            const periodEndDate = new Date(
                                item.period_end_date
                            );
                            const daysUntilExpiration = Math.ceil(
                                (periodEndDate - now) / (1000 * 60 * 60 * 24)
                            );
                            const benefitAmount = parseFloat(
                                item.benefit?.amount || 0
                            );
                            const usedAmount = parseFloat(
                                item.current_period_usage || 0
                            );
                            const amountLeft = benefitAmount - usedAmount;

                            return {
                                benefit_id: item.benefit?.id,
                                benefit_title: item.benefit?.benefit_title,
                                card_benefit_id: item.benefit?.card_benefit_id,
                                card_id: item.card?.id,
                                card_title: item.card?.title,
                                card_image_url:
                                    item.card?.image?.url +
                                    '/_small/' +
                                    item.card?.image?.file_name,
                                card_last_four:
                                    item.connected_account_last_four,
                                amount: benefitAmount,
                                amount_used: usedAmount,
                                amount_left: amountLeft,
                                period_end_date: item.period_end_date,
                                days_until_expiration: Math.max(
                                    0,
                                    daysUntilExpiration
                                ),
                                connected_account_id: item.connected_account_id,
                            };
                        })
                        .sort(
                            (a, b) =>
                                a.days_until_expiration -
                                b.days_until_expiration
                        );

                    this.expiringBenefits = expiringBenefits;
                })
                .catch((response) => {
                    notify(
                        {
                            group: 'error',
                            title: 'Error',
                            text: 'There was a problem trying to get expiring benefits.',
                        },
                        4000
                    );
                });
        },
        setUser(user) {
            if (!user || this.user?.uuid === user.uuid) {
                return;
            }

            if (this.isEditMode) {
                this.cancelEditMode();
            }

            this.users.forEach((u) => {
                u.active = false;
            });
            this.users[this.users.indexOf(user)].active = true;
            this.user = user;

            // Immediately reset all dashboard data for instant visual feedback
            this.resetDashboardData();

            // Load selected user's widget preferences and dashboard data
            this.fetchWidgetPreferences();
            this.getDashboardCards();
        },
        setWalletId(walletId) {
            this.walletId = walletId;
        },
    },
});
